// Cart.tsx
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Button from "components/common/Button";
import api from "config/api";
import { useAppDispatch, useAppSelector } from "hooks/store";
// === ADDED BY SAMPATH – 2026-01-12 ===
// FIX: iPhone (Safari) layout issues -> dynamic footer height + iOS smooth scrolling
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type TouchEvent } from "react";
// === END SAMPATH ===
import {
  removeAllFromCart,
  removeFromCart,
  addToCart,
  setCheckoutData,
  setSelected,
  // === ADDED BY SAMPATH – 2026-01-12 ===
  toggleCart,
  // === END SAMPATH ===
  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Delivery type switch (Pickup/Delivery) from cart
  setdeliveryType,
  // === END SAMPATH ===
} from "store/slices/main.slice";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { format } from "date-fns";

// === ADDED BY SAMPATH – 2026-01-12 ===
import CloseIcon from "@mui/icons-material/Close";
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-29 ===
// Cart qty / remove icon buttons
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-03-16 ===
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-03-16 ===
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-03-16 ===
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
// === END SAMPATH ===
import { convertToTimezone } from "utils";
import { ORDER, TIME_ZONES } from "utils/constants";
import { getFixedDeliveryCharge, isKmDeliveryChargesEnabled } from "utils/deliveryCharges";
import { useSearchParams } from "react-router-dom";
type Props = {
  setStage: (v: string) => void;
};

const formatGlobalTime = (timeZone: string) => {
  const formattedTime = convertToTimezone(new Date(), TIME_ZONES[timeZone]);
  const [date, time] = formattedTime.split(", ");
  const [mon, day, year] = date.split("/");
  let [hr, min, sec]: string[] | number[] = time.split(":");

  const isPM = time.includes("PM");
  hr = isPM ? (hr === "12" ? 12 : +hr + 12) : hr === "12" ? 0 : +hr;
  sec = sec ? sec.replace(/ AM| PM/, "") : "0";

  const currentTime = new Date();
  currentTime.setMonth(+mon - 1);
  currentTime.setDate(+day);
  currentTime.setFullYear(+year);
  currentTime.setHours(+hr, +min, +sec);
  return currentTime;
};

const toSafeNumber = (value: any): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatVisibleMoney = (value: number | null) => {
  if (value === null || value <= 0) return "";
  return `$${value.toFixed(2)}`;
};

const hasVisiblePriceDesc = (value: any) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().replace(/\s+/g, "");
  return !["", "0", "0.0", "0.00", "$0", "$0.0", "$0.00"].includes(normalized);
};
// === ADDED BY SAMPATH – 2026-01-05 ===
const getCartItemImage = (item: any): string => {
  return (
    item?.imageUrl ||
    item?.image ||
    item?.productImage ||
    item?.product?.imageUrl ||
    item?.product?.image ||
    item?.pizza?.imageUrl ||
    item?.pizza?.image ||
    item?.data?.imageUrl ||
    item?.data?.image ||
    ""
  );
};
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-05 ===
const sanitizeForStructuredClone = <T,>(input: T): T => {
  const seen = new WeakSet<object>();

  const isDomNode = (v: any) =>
    typeof v === "object" &&
    v !== null &&
    (typeof (v as any).nodeType === "number" ||
      typeof (v as any).ownerDocument === "object");

  const isReactElementLike = (v: any) =>
    typeof v === "object" &&
    v !== null &&
    ("$$typeof" in v || "_owner" in v || "props" in v);

  const walk = (v: any): any => {
    if (v === null) return null;
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") return v;

    if (t === "function" || t === "symbol") return undefined;
    if (t === "bigint") return v.toString();
    if (v instanceof Date) return v.toISOString();

    if (isDomNode(v) || isReactElementLike(v)) return undefined;

    if (v instanceof Map)
      return Array.from(v.entries()).map(([k, val]) => [walk(k), walk(val)]);
    if (v instanceof Set) return Array.from(v.values()).map((val) => walk(val));

    if (Array.isArray(v))
      return v.map((x) => walk(x)).filter((x) => x !== undefined);

    if (typeof v === "object") {
      if (seen.has(v)) return undefined;
      seen.add(v);

      const out: any = {};
      Object.keys(v).forEach((key) => {
        const next = walk(v[key]);
        if (next !== undefined) out[key] = next;
      });
      return out;
    }

    return undefined;
  };

  return walk(input) as T;
};
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-05 ===
const CartImage = ({ src, alt }: { src?: string; alt: string }) => {
  const [ok, setOk] = useState(true);

  if (!src || !ok) {
    return (
      // === ADDED BY SAMPATH – 2026-03-16 ===
      // Compact image box for same mobile + desktop cart item design
      <div className="h-[52px] w-[52px] rounded-lg bg-gray-100 flex items-center justify-center text-[10px] text-gray-500 shrink-0">
        No Image
      </div>
      // === END SAMPATH ===
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      // === ADDED BY SAMPATH – 2026-03-16 ===
      // Compact product thumbnail and no extra border
      className="h-[52px] w-[52px] rounded-lg object-cover bg-white shrink-0"
      // === END SAMPATH ===
      onError={() => setOk(false)}
      loading="lazy"
    />
  );
};
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-05 ===
const Pill = ({
  children,
  className = "",
}: {
  children: any;
  className?: string;
}) => (
  <div
    className={
      "px-3 py-1.5 rounded-full bg-white border border-gray-200 text-[12px] font-bold text-gray-600 shadow-sm " +
      className
    }
  >
    {children}
  </div>
);

const DividerDot = () => (
  <div className="flex-1 border-b border-dashed border-gray-300 mx-3 translate-y-[-2px]" />
);
// === END SAMPATH ===

const SHOW_OLD_DEAL_DETAIL_BLOCK = true;

const stripAutoDealNotes = (value: any) =>
  String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("** No - "))
    .join("\n")
    .trim();

const stripDisplayOnlyPizzaNoteLines = (value: any) =>
  String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !/^size\s*:/i.test(line) &&
        !/^spice\s*:/i.test(line) &&
        !/^toppings\s*:/i.test(line)
    )
    .join("\n")
    .trim();

const formatModifierDisplayName = (modifier: any) => {
  const name = String(
    modifier?.description || modifier?.pizzaModifierName || modifier?.modifierName || "",
  ).trim();
  if (!name) return "";

  const parts: string[] = [];
  const region = Number(modifier?.pizzaRegion ?? modifier?.PizzaRegion ?? 1);
  const multiplier = Number(
    modifier?.Multiplier ||
      modifier?.DtltoppMultiplier ||
      modifier?.legacyQuantity ||
      modifier?.quantity ||
      1
  );
  if (multiplier > 1) parts.push(`${multiplier}x`);
  if (region === 2) parts.push("L");
  if (region === 3) parts.push("R");

  return parts.length ? `${parts.join("-")} ${name}` : name;
};

const formatSelectedOptionDisplayName = (item: any) => {
  const name = String(item?.name || item?.description || item?.fullDescription || "").trim();
  if (!name) return "";

  const price = Number(item?.price || item?.unitPrice || item?.subTotal || 0);
  return price > 0 ? `${name} [$${price.toFixed(2)}]` : name;
};

const shouldShowCartModifier = (modifier: any, showDefaultToppings: any) => {
  const isDefault = Number(modifier?.defaultTopping || 0) === 1;
  const isDouble =
    Number(modifier?.Multiplier || modifier?.DtltoppMultiplier || modifier?.legacyQuantity || 1) > 1;
  const isPartial = [2, 3].includes(Number(modifier?.pizzaRegion ?? modifier?.PizzaRegion ?? 1));
  const hasCharge = Number(modifier?.subTotal || modifier?.totalValue || 0) > 0;

  return (
    showDefaultToppings ||
    !isDefault ||
    modifier?.newDefaultTopping ||
    isDouble ||
    isPartial ||
    hasCharge
  );
};

const CartNoteText = ({ value }: { value: any }) => {
  const lines = String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return null;

  return (
    <>
      {lines.map((line, index) => {
        const isRemovedDefault = /^\*\*\s*no\s*-/i.test(line);

        return (
          <span
            key={`${line}-${index}`}
            className={isRemovedDefault ? "block text-[#9f1239]" : "block"}
            style={
              isRemovedDefault
                ? {
                    textDecorationLine: "line-through",
                    textDecorationColor: "#be123c",
                    textDecorationThickness: "1px",
                  }
                : undefined
            }
          >
            {line}
          </span>
        );
      })}
    </>
  );
};

const Item = ({ item, index, onAction, showDefaultToppings = 0 }: any) => {
  const toppings = useMemo(
    () =>
      item?.modifiers?.filter(
        (i: any) => shouldShowCartModifier(i, showDefaultToppings)
      ),
    [showDefaultToppings, item?.modifiers]
  );

  const isDeal =
    item.data &&
    item.data.selectedDealPizzas &&
    item.data.selectedDealPizzas.length > 0;

  const productImg = getCartItemImage(item);

  // === ADDED BY SAMPATH – 2026-01-29 ===
  const qty = Number(item?.quantity ?? 1) || 1;
  // === END SAMPATH ===

  const dealPizzaDetails = useMemo(() => {
    if (!isDeal || !Array.isArray(item?.data?.selectedDealPizzas)) return [];

    return item.data.selectedDealPizzas
      .filter(
        (dealPizza: any) =>
          !!dealPizza &&
          (!!dealPizza?.pizzaName ||
            !!dealPizza?.fullDescription ||
            !!dealPizza?.pizzaSize?.size ||
            !!dealPizza?.modifiers?.length ||
            !!dealPizza?.note)
      )
      .map((dealPizza: any, dealIndex: number) => {
        const pizzaTitle =
          dealPizza?.pizzaName ??
          dealPizza?.fullDescription ??
          `Pizza ${dealIndex + 1}`;
        const pizzaSizeObj =
          dealPizza?.pizzaSize || dealPizza?.config?.existingPizzaSize || null;
        const pizzaSize = pizzaSizeObj?.size || dealPizza?.config?.size || "";
        const pizzaCrustObj =
          dealPizza?.pizzaCrust || dealPizza?.config?.existingPizzaCrust || null;
        const pizzaCrust = pizzaCrustObj?.crustName || "";
        const pizzaCrustPrice = Number(pizzaCrustObj?.crustPrice || 0);
        const pizzaNote = stripDisplayOnlyPizzaNoteLines(
          dealPizza?.note || dealPizza?.config?.note || ""
        );

        const pizzaToppings = (dealPizza?.modifiers || [])
          .filter((modifier: any) => shouldShowCartModifier(modifier, showDefaultToppings))
          .map((modifier: any) => {
            let label = formatModifierDisplayName(modifier);
            if (modifier.totalValue) {
              label += ` [$${Number(modifier.totalValue).toFixed(2)}]`;
            }
            return label;
          })
          .filter(Boolean);

        return {
          title: pizzaTitle,
          size: pizzaSize,
          crust:
            pizzaCrust && pizzaCrustPrice
              ? `${pizzaCrust} [$${pizzaCrustPrice.toFixed(2)}]`
              : pizzaCrust,
          toppings: pizzaToppings,
          note: pizzaNote,
        };
      });
  }, [isDeal, item?.data?.selectedDealPizzas, showDefaultToppings]);

  const dealSelectedOptions = useMemo(() => {
    if (!isDeal || !Array.isArray(item?.selectedItemsLevel)) return [];

    return item.selectedItemsLevel
      .map((selectedItem: any) => formatSelectedOptionDisplayName(selectedItem))
      .filter(Boolean);
  }, [isDeal, item?.selectedItemsLevel]);

  const visibleDealLevelNote = useMemo(
    () =>
      isDeal
        ? stripDisplayOnlyPizzaNoteLines(
            stripAutoDealNotes(item?.note || item?.data?.config?.note || "")
          )
        : "",
    [isDeal, item?.data?.config?.note, item?.note],
  );

  const visibleItemNote = useMemo(
    () => stripDisplayOnlyPizzaNoteLines(item?.note || item?.data?.config?.note || ""),
    [item?.data?.config?.note, item?.note],
  );

  const displayItemPrice = useMemo(() => {
    if (isDeal && dealPizzaDetails.length > 0) {
      return Number(item?.subTotal || 0);
    }

    return Number(item?.subTotal || 0);
  }, [isDeal, dealPizzaDetails, item?.subTotal]);

  // === ADDED BY SAMPATH – 2026-03-05 ===
  // FIX: prevent crash when pizzaSize is missing (undefined) + log which item caused it
  const safeItemPizzaSize = item?.pizzaSize || null;

  useEffect(() => {
    if (item?.isPizza && !safeItemPizzaSize) {
      // This will show you the exact product/item causing the issue
      console.warn("[CART] Missing pizzaSize on item", {
        index,
        fullDescription: item?.fullDescription,
        productName: item?.productName,
        isDeal,
        item,
      });
    }

    if (isDeal && Array.isArray(item?.data?.selectedDealPizzas)) {
      item.data.selectedDealPizzas.forEach((dp: any, dpIndex: number) => {
        if (!dp?.pizzaSize) {
          console.warn("[CART] Missing pizzaSize on deal pizza", {
            index,
            dealIndex: dpIndex,
            dealPizzaName: dp?.pizzaName ?? dp?.fullDescription,
            fullDescription: item?.fullDescription,
            item,
            dealPizza: dp,
          });
        }
      });
    }
  }, [index, item, isDeal, safeItemPizzaSize]);
  // === END SAMPATH ===

  return (
    <div
      // === ADDED BY SAMPATH – 2026-03-16 ===
      // One clean list style for both mobile + desktop, remove card border/shadow look
      className="mx-3 border-b border-gray-200 last:border-b-0"
      // === END SAMPATH ===
      onClick={() => onAction("edit", { ...item, index, isEdit: true })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onAction("edit", { ...item, index, isEdit: true });
        }
      }}
    >
      {/* === ADDED BY SAMPATH – 2026-03-16 === */}
      {/* Same item layout on all devices, spacing reduced to match provided design */}
      <div className="pt-3 pb-1.5 flex items-start gap-3">
        <div className="self-start pt-0.5">
          <CartImage src={productImg} alt={item?.fullDescription || "Item"} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[14px] sm:text-[13px] font-[600] uppercase tracking-[0.01em]  break-words">
            {item.fullDescription}
          </p>

          {isDeal ? (
            <div className="mt-1">
              {SHOW_OLD_DEAL_DETAIL_BLOCK ? (
                <div className="text-[12px] text-gray-700 break-words">
                  <p className="font-semibold text-[#b91c1c] mb-2">Deal Items:</p>
                  {dealPizzaDetails.map((dealPizza: any, dealIndex: number) => (
                    <div
                      key={`${dealPizza.title}-${dealIndex}`}
                      className={`py-1.5 ${dealIndex > 0 ? "border-t border-[#f0d7d7] mt-2" : ""}`}
                    >
                      <p>
                        <span className="font-semibold text-[#b91c1c]">
                          Pizza {dealIndex + 1}:
                        </span>{" "}
                        {dealPizza.title}
                      </p>
                      <p>
                        <span className="font-semibold">Size:</span>{" "}
                        {dealPizza.size || "--"}
                      </p>
                      <p>
                        <span className="font-semibold">Crust:</span>{" "}
                        {dealPizza.crust || "--"}
                      </p>
                      <p>
                        <span className="font-semibold">Toppings:</span>{" "}
                        {dealPizza.toppings.length ? dealPizza.toppings.join(", ") : ""}
                      </p>
                      <p>
                        <span className="font-semibold">Note:</span>{" "}
                        <CartNoteText value={dealPizza.note || ""} />
                      </p>
                    </div>
                  ))}
                  {!!dealSelectedOptions.length && (
                    <p className="mt-1">
                      <span className="font-semibold">Options:</span>{" "}
                      {dealSelectedOptions.join(", ")}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-[12px] text-gray-700 break-words">
                  {item.data.selectedDealPizzas
                    .map(
                      (dealPizza: any) =>
                        dealPizza?.pizzaName ?? dealPizza?.fullDescription
                    )
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}

              {!!visibleDealLevelNote && (
                <p className="text-[12px] text-gray-700 break-words">
                  <CartNoteText value={visibleDealLevelNote} />
                </p>
              )}
            </div>
          ) : (
            <div className="mt-1 space-y-1">
              {!!item.isPizza && (
                <p className="text-[12px] text-gray-700 break-words">
                  {[
                    safeItemPizzaSize?.size
                      ? safeItemPizzaSize?.size +
                        (safeItemPizzaSize?.price
                          ? ` [$${safeItemPizzaSize.price}]`
                          : "")
                      : "",
                    item?.spiceLevel?.spicename || "",
                    item?.pizzaCrust?.crustName
                      ? `${item.pizzaCrust?.crustName}${
                          item.pizzaCrust?.crustPrice
                            ? ` [$${item.pizzaCrust?.crustPrice}]`
                            : ""
                        }`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}

              {!item.isPizza && item.selectedItemsLevel?.length > 0 && (
                <p className="text-[10px] text-gray-600 break-words">
                  {item.selectedItemsLevel
                    .map(
                      (i: any) => i.name + (!!i.price ? " $" + i.price : "")
                    )
                    .join(", ")}
                </p>
              )}

              {!!toppings?.length && (
                <p className="text-[12px] text-gray-600 break-words">
                  <span className="font-bold text-gray-800">TOPPINGS: </span>
                  {toppings
                    .map((i: any) => {
                      let v = formatModifierDisplayName(i);
                      if (!i.newDefaultTopping && i.subTotal) {
                        v += ` [$${i.subTotal}]`;
                      }
                      return v;
                    })
                    .join(", ")}
                </p>
              )}

              {!!visibleItemNote && (
                <p className="text-[12px] text-gray-700 break-words">
                  <CartNoteText value={visibleItemNote} />
                </p>
              )}
            </div>
          )}

          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-[13px] sm:text-[13px] font-[700] text-gray-900">
              ${displayItemPrice.toFixed(2)}
            </p>

            <div className="shrink-0 flex items-center pb-0.5">
              <div
                className="flex h-9 items-center rounded-[16px] bg-gray-50 text-gray-950 shadow-sm"
                style={{
                  boxShadow: "inset 0 0 0 1px rgb(17 24 39 / 0.04)",
                }}
              >
                {qty === 1 ? (
                  <button
                    type="button"
                    aria-label="Remove item"
                    className="h-9 w-9 rounded-l-[16px] flex items-center justify-center active:scale-[0.98]"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("delete", { index, item });
                    }}
                  >
                    <DeleteIcon
                      className="!text-[18px]"
                      sx={{ color: "rgb(17 24 39 / 1)" }}
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    className="h-9 w-9 rounded-l-[16px] flex items-center justify-center active:scale-[0.98]"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("dec", { index, item });
                    }}
                  >
                    <RemoveIcon
                      className="!text-[18px]"
                      sx={{ color: "rgb(17 24 39 / 1)" }}
                    />
                  </button>
                )}

                <div className="min-w-[24px] text-center text-[13px] font-[800] text-gray-950">
                  {qty}
                  <span className="ml-1 text-[11px] font-[700]">×</span>
                </div>

                <button
                  type="button"
                  aria-label="Increase quantity"
                  className="h-9 w-9 rounded-r-[16px] flex items-center justify-center active:scale-[0.98]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction("inc", { index, item });
                  }}
                >
                  <AddIcon
                    className="!text-[18px]"
                    sx={{ color: "rgb(17 24 39 / 1)" }}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* === END SAMPATH === */}

      <div className="hidden">
        <div
          className="bg-primary rounded-md h-[22px] w-[22px] flex justify-center items-center cursor-pointer"
          onClick={() => onAction("edit", { ...item, index, isEdit: true })}
        >
          <EditIcon className="!text-[15px] !text-light-text" />
        </div>
        <div
          className="bg-primary rounded-md h-[22px] w-[22px] flex justify-center items-center cursor-pointer"
          onClick={() => onAction("delete", { index, item })}
        >
          <DeleteIcon className="!text-[15px] !text-light-text" />
        </div>
      </div>
    </div>
  );
};

const CartView = ({ setStage }: Props) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const cleanFontStack = '"Noto Sans", sans-serif';

  const { cart, taxes, settings, checkoutData, deliveryType, companyData, validTimes, sideBarMenu } =
    useAppSelector(({ main }) => main);
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const relatedRowRef = useRef<HTMLDivElement | null>(null);
  const relatedDragStartXRef = useRef(0);
  const relatedDragScrollLeftRef = useRef(0);
  const relatedIsDraggingRef = useRef(false);
  const relatedAutoScrollPausedRef = useRef(false);

  const isCartOpen = useAppSelector(({ main }: any) => {
    const v =
      main?.isCartOpen ??
      main?.cartOpen ??
      main?.openCart ??
      main?.cartDrawerOpen ??
      main?.isCartDrawerOpen ??
      main?.showCart ??
      undefined;
    return typeof v === "boolean" ? v : true;
  });

  const [orderType, setOrderType] = useState("");
  const activeCategoryId = useMemo(() => Number(searchParams.get("product") || 0), [searchParams]);
  const recommendationCategories = useMemo(() => {
    const ordered = [...sideBarMenu];
    const activeIndex = ordered.findIndex((item) => Number(item.id) === activeCategoryId);
    if (activeIndex > 0) {
      const [activeItem] = ordered.splice(activeIndex, 1);
      ordered.unshift(activeItem);
    }
    return ordered.slice(0, 3);
  }, [activeCategoryId, sideBarMenu]);
  const cartProductIdsKey = useMemo(
    () =>
      Array.from(
        new Set((cart || []).map((item: any) => Number(item?.productId || 0)).filter(Boolean)),
      )
        .sort((a, b) => a - b)
        .join(","),
    [cart],
  );

  const getSuggestedProductPriceText = (item: any) => {
    const safePrice = toSafeNumber(item?.price);
    const safeDeliveryPrice = toSafeNumber(item?.deliveryprice);
    const safeTakeoutPrice = toSafeNumber(item?.takeoutprice);

    if (settings.ApplyTakeOutDeliveryPrice !== 0) {
      if (deliveryType !== "pickup") {
        const resolvedDeliveryPrice =
          safeDeliveryPrice === 0 || safeDeliveryPrice === null
            ? safePrice
            : safeDeliveryPrice;
        return formatVisibleMoney(resolvedDeliveryPrice);
      }

      const resolvedTakeoutPrice =
        safeTakeoutPrice === 0 || safeTakeoutPrice === null
          ? safePrice
          : safeTakeoutPrice;
      return formatVisibleMoney(resolvedTakeoutPrice);
    }

    return formatVisibleMoney(safePrice);
  };

  const getSuggestedProductSubPriceText = (item: any) => {
    const visiblePrice = getSuggestedProductPriceText(item);
    if (visiblePrice) return visiblePrice;
    if (hasVisiblePriceDesc(item?.pricedesc)) return String(item.pricedesc);
    return "";
  };
  const [deleteDialog, setDeleteDialog] = useState<any>(undefined);

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // TEMP: Hide Delivery Charge + Taxes. Later enable by setting true.
  const SHOW_PRICE_BREAKDOWN = false;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-02-02 ===
  // Header company info (companyName + address/locationText). address & locationText are same.
  const companyHeaderName = useMemo(() => {
    return companyData?.companyName || "Company name";
  }, [companyData, settings]);

  const companyHeaderAddress = useMemo(() => {
    const v = companyData?.companyName || companyData?.address2 || "Address";
    return String(v || "").trim();
  }, [companyData, settings]);

  // === ADDED BY SAMPATH – 2026-03-16 ===
  // Pickup location should use company address only
  const pickupLocationAddress = useMemo(() => {
    const parts = [
      companyData?.address1 ,
      companyData?.address2 
  
    ]
      .map((v) => String(v || "").trim())
      .filter(Boolean);

    return parts.length ? parts.join("") : companyHeaderAddress;
  }, [companyData, companyHeaderAddress]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-03-16 ===
  // Scheduled text shown in cart info card when available
  const scheduledText = useMemo(() => {
    const cd: any = checkoutData || {};
    return (
      cd?.scheduleText ||
      cd?.scheduledLabel ||
      cd?.scheduledAt ||
      cd?.scheduleDateTime ||
      cd?.scheduleDisplay ||
      cd?.timeLabel ||
      ""
    );
  }, [checkoutData]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-02-02 ===
  // NEW: Premium modern "bluer / food app" header look using themeColor (subtle gradient + glass)
  const cartHeaderStyle = useMemo(() => {
    return {
      background:
        "linear-gradient(140deg, rgb(255 255 255 / 0.84), rgb(var(--color-primary) / 0.12) 45%, rgb(255 255 255 / 0.74) 100%)",
      borderBottom: "1px solid rgb(var(--color-primary) / 0.12)",
      boxShadow: "0 14px 34px rgb(15 23 42 / 0.07)",
      backdropFilter: "blur(18px)",
    } as React.CSSProperties;
  }, []);
  // === END SAMPATH ===
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Recalc totals when qty changes (matches ProductPopUp.tsx style: subTotal * rate => taxes, itemTotal = subTotal + taxes)
  const buildQtyUpdatedCartItem = (
    srcItem: any,
    index: number,
    nextQty: number
  ) => {
    const prevQty = Math.max(1, Number(srcItem?.quantity ?? 1) || 1);
    const qty = Math.max(1, Number(nextQty) || 1);

    const unitSubTotal = Number(srcItem?.subTotal || 0) / prevQty;
    const subTotal = +(unitSubTotal * qty);

    const taxRate1 = Number(srcItem?.taxRate1 ?? 0) || 0;
    const taxRate2 = Number(srcItem?.taxRate2 ?? 0) || 0;
    const taxRate3 = Number(srcItem?.taxRate3 ?? 0) || 0;

    const totalTax1 = srcItem?.taxId1
      ? +((subTotal * taxRate1) / 100).toFixed(5)
      : 0;
    const totalTax2 = srcItem?.taxId2
      ? +((subTotal * taxRate2) / 100).toFixed(5)
      : 0;
    const totalTax3 = srcItem?.taxId3
      ? +((subTotal * taxRate3) / 100).toFixed(5)
      : 0;

    const itemTotal = +(subTotal + totalTax1 + totalTax2 + totalTax3);

    const totalUnitPrice =
      Number(srcItem?.totalUnitPrice ?? 0) || +unitSubTotal;

    const safeConfig = sanitizeForStructuredClone(srcItem?.data?.config || {});
    safeConfig.qty = qty;

    const updated = {
      ...srcItem,
      quantity: qty,
      subTotal,
      totalTax1,
      totalTax2,
      totalTax3,
      itemTotal,
      totalUnitPrice,
      data: {
        ...(srcItem?.data || {}),
        config: safeConfig,
        isEdit: true,
        index,
      },
    };

    return sanitizeForStructuredClone(updated);
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-15 ===
  const cartTotalQty = useMemo(() => {
    return (cart || []).reduce(
      (sum: number, i: any) => sum + (Number(i?.quantity) || 0),
      0
    );
  }, [cart]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-15 ===
  useEffect(() => {
    if (!isCartOpen) return;
    const isEmpty = !(cart && cart.length);
    const isQtyZero = cartTotalQty <= 0;
    if (isEmpty || isQtyZero) {
      setDeleteDialog(undefined);
      dispatch(toggleCart());
    }
  }, [isCartOpen, cart?.length, cartTotalQty, dispatch]);

  useEffect(() => {
    if (!isCartOpen || !recommendationCategories.length) {
      setRelatedProducts([]);
      return;
    }

    let cancelled = false;
    const cartProductIds = new Set(
      cartProductIdsKey
        .split(",")
        .map((value) => Number(value))
        .filter(Boolean),
    );

    const run = async () => {
      setRelatedLoading(true);
      try {
        const results = await Promise.all(
          recommendationCategories.map(async (category) => {
            const params = new URLSearchParams();
            params.set("categoryId", String(category.id));
            const { data } = await api.get("/api/Product/ByCategoryId?" + params.toString());

            return (data || [])
              .filter((item: any) => !cartProductIds.has(Number(item?.productId || 0)))
              .slice(0, 2)
              .map((item: any) => ({
                ...item,
                __categoryName: category.categoryDescription,
                __categoryId: category.id,
              }));
          })
        );

        if (cancelled) return;

        setRelatedProducts(results.flat().slice(0, 8));
      } catch {
        if (!cancelled) setRelatedProducts([]);
      } finally {
        if (!cancelled) setRelatedLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [cartProductIdsKey, isCartOpen, recommendationCategories]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  const footerRef = useRef<HTMLDivElement | null>(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchDeltaXRef = useRef(0);
  const touchTrackingRef = useRef(false);
  const [footerHeight, setFooterHeight] = useState(0);

  useLayoutEffect(() => {
    if (!isCartOpen) return;
    const el = footerRef.current;
    if (!el) return;

    const update = () => setFooterHeight(el.getBoundingClientRect().height || 0);
    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [isCartOpen, cart?.length]);

  useEffect(() => {
    const node = relatedRowRef.current;
    if (!node || !isCartOpen || relatedProducts.length <= 1) return;

    let raf = 0;
    let direction = 1;
    let lastTs = 0;

    const tick = (ts: number) => {
      if (!node) return;

      if (!lastTs) lastTs = ts;
      const delta = ts - lastTs;
      lastTs = ts;

      if (!relatedAutoScrollPausedRef.current) {
        const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
        if (maxScroll > 0) {
          const next = node.scrollLeft + direction * delta * 0.035;

          if (next >= maxScroll) {
            node.scrollLeft = maxScroll;
            direction = -1;
          } else if (next <= 0) {
            node.scrollLeft = 0;
            direction = 1;
          } else {
            node.scrollLeft = next;
          }
        }
      }

      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [isCartOpen, relatedProducts]);

  const handleRelatedWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const node = relatedRowRef.current;
    if (!node) return;

    relatedAutoScrollPausedRef.current = true;
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      node.scrollLeft += event.deltaY;
    }
  };

  const handleRelatedMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const node = relatedRowRef.current;
    if (!node) return;

    relatedIsDraggingRef.current = true;
    relatedAutoScrollPausedRef.current = true;
    relatedDragStartXRef.current = event.clientX;
    relatedDragScrollLeftRef.current = node.scrollLeft;
  };

  const handleRelatedMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const node = relatedRowRef.current;
    if (!node || !relatedIsDraggingRef.current) return;

    const distance = event.clientX - relatedDragStartXRef.current;
    if (Math.abs(distance) > 4) {
      node.scrollLeft = relatedDragScrollLeftRef.current - distance;
    }
  };

  const stopRelatedDrag = () => {
    relatedIsDraggingRef.current = false;
  };

  const pauseRelatedAutoScroll = () => {
    relatedAutoScrollPausedRef.current = true;
  };

  const resumeRelatedAutoScroll = () => {
    if (relatedIsDraggingRef.current) return;
    relatedAutoScrollPausedRef.current = false;
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-14 ===
  const REMOVE_DIALOG_ZINDEX = 10050;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-15 ===
  const todayName = useMemo(() => {
    const now = companyData?.timeZone ? formatGlobalTime(companyData.timeZone) : new Date();
    return format(now, "EEEE");
  }, [companyData?.timeZone]);

  const pickupTimeConfig = useMemo(
    () =>
      validTimes.find(
        (item: any) => item.timDay === todayName && item.timOrdtype === ORDER.TAKE_OUT
      ),
    [todayName, validTimes]
  );

  const deliveryTimeConfig = useMemo(
    () =>
      validTimes.find(
        (item: any) => item.timDay === todayName && item.timOrdtype === ORDER.DELIVERY
      ),
    [todayName, validTimes]
  );

  const isPickupDisabled = !!settings?.ClosePickupOrder;
  const isDeliveryDisabled = !!settings?.CloseDeliveryOrder;

  const changeDeliveryType = (next: "pickup" | "delivery") => {
    if (next === "pickup" && isPickupDisabled) return;
    if (next === "delivery" && isDeliveryDisabled) return;
    dispatch(setdeliveryType(next));
  };
  // === END SAMPATH ===

  useEffect(() => {
    if (settings.ClosePickupOrder) {
      setOrderType(ORDER.DELIVERY);
      return;
    }
    if (settings.CloseDeliveryOrder) {
      setOrderType(ORDER.TAKE_OUT);
      return;
    }
    setOrderType(checkoutData?.orderType || ORDER.TAKE_OUT);
    if (settings.ApplyTakeOutDeliveryPrice !== 0) {
      if (deliveryType !== "pickup") {
        setOrderType(ORDER.DELIVERY);
      } else {
        setOrderType(ORDER.TAKE_OUT);
      }
    } else {
      if (deliveryType !== "pickup") {
        setOrderType(ORDER.DELIVERY);
      } else {
        setOrderType(ORDER.TAKE_OUT);
      }
    }
  }, [checkoutData, deliveryType, settings]);

  useEffect(() => {
    if (!isCartOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isCartOpen]);

  const handleDrawerTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    const touch = event.touches[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    touchDeltaXRef.current = 0;
    touchTrackingRef.current = true;
  };

  const handleDrawerTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!touchTrackingRef.current) return;
    const touch = event.touches[0];
    if (!touch) return;
    touchDeltaXRef.current = touch.clientX - touchStartXRef.current;
  };

  const handleDrawerTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (!touchTrackingRef.current) return;
    touchTrackingRef.current = false;
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    touchDeltaXRef.current = 0;

    if (deltaX > 80 && Math.abs(deltaX) > Math.abs(deltaY)) {
      dispatch(toggleCart());
    }
  };

  const placeOrder = () => {
    // === ADDED BY SAMPATH – 2026-01-15 ===
    setDeleteDialog(undefined);
    dispatch(toggleCart());

    const openDetails = () => setStage("DETAILS");
    if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
      window.requestAnimationFrame(openDetails);
    } else {
      setTimeout(openDetails, 0);
    }
    // === END SAMPATH ===

    const safeCart = sanitizeForStructuredClone(cart);
    dispatch(setCheckoutData({ orderType, finalBill, total, cart: safeCart }));
  };

  const handleCartAction = (type: string, data: any) => {
    if (type === "edit") {
      // === ADDED BY SAMPATH – 2026-01-15 ===
      setDeleteDialog(undefined);
      if (isCartOpen) {
        dispatch(toggleCart());
        const safeSelected = sanitizeForStructuredClone(data);
        const openPopup = () => dispatch(setSelected(safeSelected));
        if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
          window.requestAnimationFrame(openPopup);
        } else {
          setTimeout(openPopup, 0);
        }
      } else {
        const safeSelected = sanitizeForStructuredClone(data);
        dispatch(setSelected(safeSelected));
      }
      return;
      // === END SAMPATH ===
    }

    // === ADDED BY SAMPATH – 2026-01-29 ===
    if (type === "inc") {
      const srcItem = data?.item;
      const index = data?.index;
      const nextQty = (Number(srcItem?.quantity ?? 1) || 1) + 1;

      const updated = buildQtyUpdatedCartItem(srcItem, index, nextQty);
      dispatch(addToCart(updated));
      return;
    }

    if (type === "dec") {
      const srcItem = data?.item;
      const index = data?.index;
      const currQty = Number(srcItem?.quantity ?? 1) || 1;
      const nextQty = Math.max(1, currQty - 1);

      const updated = buildQtyUpdatedCartItem(srcItem, index, nextQty);
      dispatch(addToCart(updated));
      return;
    }
    // === END SAMPATH ===

    if (type === "delete") {
      setDeleteDialog(data);
    }
  };

  const openSuggestedProduct = (item: any) => {
    const safeSelected = sanitizeForStructuredClone(item);
    setDeleteDialog(undefined);

    if (isCartOpen) {
      dispatch(toggleCart());
      const openPopup = () => dispatch(setSelected(safeSelected));
      if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
        window.requestAnimationFrame(openPopup);
      } else {
        setTimeout(openPopup, 0);
      }
      return;
    }

    dispatch(setSelected(safeSelected));
  };

  const deliveryCharge = useMemo(() => {
    if (orderType !== ORDER.DELIVERY) return 0;
    if (isKmDeliveryChargesEnabled(settings)) return 0;
    return getFixedDeliveryCharge(companyData);
  }, [orderType, companyData, settings]);

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Return BOTH subtotal (before tax) + total (after tax) for later enable
  const { finalBill, total, subTotal } = useMemo(() => {
    const list: any[] = [
      { title: "SubTotal", amt: 0 },
      ...taxes.map((i) => ({ title: i.name, amt: 0 })),
    ];
    let allTaxes = 0;

    const subTotalCalc = cart.reduce((t, item) => {
      const itemSubTotal =
        item.data && item.data.selectedDealPizzas
          ? Number(item?.subTotal || 0)
          : item.subTotal;

      if (item.totalTax1 && item.taxId1 > 0) {
        list[1].amt += item.totalTax1;
        allTaxes += item.totalTax1;
      }
      if (item.totalTax2 && item.taxId2 > 0) {
        list[2].amt += item.totalTax2;
        allTaxes += item.totalTax2;
      }
      if (item.totalTax3 && item.taxId3 > 0) {
        list[3].amt += item.totalTax3;
        allTaxes += item.totalTax3;
      }
      return t + itemSubTotal;
    }, 0);

    list[0].amt = subTotalCalc;

    return {
      finalBill: list,
      total: subTotalCalc + allTaxes,
      subTotal: subTotalCalc,
    };
  }, [cart, taxes]);
  // === END SAMPATH ===

  return (
    <div
      className={
        "fixed inset-0 z-[9999] " +
        (isCartOpen ? "pointer-events-auto" : "pointer-events-none")
      }
      aria-hidden={!isCartOpen}
    >
      {/* Overlay */}
      <div
        className={
          "absolute inset-0 transition-opacity duration-300 " +
          (isCartOpen ? "opacity-100" : "opacity-0")
        }
        style={{
          // === ADDED BY SAMPATH – 2026-01-30 ===
          // OUTSIDE CART: do NOT blur, keep background 100% visible
          backgroundColor: "rgba(0,0,0,0)",
          backdropFilter: "none",
          // === END SAMPATH ===
        }}
        onClick={() => dispatch(toggleCart())}
      />

      {/* Drawer Panel */}
      <div
        className={
          "absolute right-0 top-0 h-full w-full sm:w-[420px] sm:max-w-[420px] bg-white border-l border-gray-200 shadow-2xl " +
          "transition-transform duration-300 ease-out " +
          (isCartOpen ? "translate-x-0" : "translate-x-full")
        }
        style={{
          backgroundColor: "rgb(255 255 255 / 0.94)",
          backdropFilter: "blur(8px)",
          height: "100dvh",
          fontFamily: '"Noto Sans", sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleDrawerTouchStart}
        onTouchMove={handleDrawerTouchMove}
        onTouchEnd={handleDrawerTouchEnd}
      >
        <div className="h-full flex flex-col">
          {/* Sticky top mini header */}
          <div className="shrink-0 border-b border-gray-200">
            <div
              className="px-4 pb-3 flex items-center justify-between gap-3"
              style={{
                ...cartHeaderStyle,
                paddingTop: "calc(0.75rem + env(safe-area-inset-top))",
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[20px] leading-none font-[900] tracking-[-0.03em] text-gray-950">
                  Your Cart
                </div>
                <div className="mt-1 text-[11px] font-semibold leading-tight text-gray-600 truncate">
                  {companyHeaderAddress}
                </div>
              </div>

              <div className="shrink-0 flex items-center gap-3">
                <div
                  className="flex h-11 min-w-[64px] items-center justify-center gap-2 rounded-full border px-3"
                  style={{
                    background:
                      "linear-gradient(135deg, rgb(255 255 255 / 0.76), rgb(var(--color-primary) / 0.08))",
                    borderColor: "rgb(var(--color-primary) / 0.16)",
                    boxShadow:
                      "inset 0 1px 0 rgb(255 255 255 / 0.75), 0 10px 22px rgb(15 23 42 / 0.08)",
                    backdropFilter: "blur(16px)",
                  }}
                >
                  <ShoppingBagOutlinedIcon
                    className="!text-[18px]"
                    style={{ color: "rgb(var(--color-primary))" }}
                  />
                  <span
                    className="text-[18px] font-[900] leading-none"
                    style={{ color: "rgb(var(--color-primary))" }}
                  >
                    {cartTotalQty || 0}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => dispatch(toggleCart())}
                  className="h-10 w-10 rounded-full border flex items-center justify-center"
                  aria-label="Close cart"
                  style={{
                    background:
                      "linear-gradient(135deg, rgb(255 255 255 / 0.88), rgb(var(--color-primary) / 0.06))",
                    borderColor: "rgb(var(--color-primary) / 0.14)",
                    boxShadow:
                      "inset 0 1px 0 rgb(255 255 255 / 0.8), 0 10px 22px rgb(15 23 42 / 0.08)",
                    backdropFilter: "blur(16px)",
                  }}
                >
                  <CloseIcon className="!text-[20px] !text-gray-800" />
                </button>
              </div>
            </div>
          </div>

          {/* Order Bar */}
          <div className="shrink-0 bg-white border-b border-gray-200">
            <div className="px-3 py-2.5">
              <div className="rounded-[22px] border border-gray-200 bg-white shadow-sm px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="relative inline-grid grid-cols-2 rounded-full bg-[#f3f4f6] p-1 shadow-inner">
                      <span
                        className={`absolute bottom-1 top-1 w-[calc(50%-4px)] rounded-full bg-white shadow-[0_1px_4px_rgba(15,23,42,0.14)] transition-transform duration-300 ease-out ${
                          deliveryType === "pickup" ? "translate-x-full" : ""
                        }`}
                        style={{ left: "4px" }}
                      />
                      <button
                        type="button"
                        disabled={isDeliveryDisabled}
                        onClick={() => changeDeliveryType("delivery")}
                        className={
                          "relative z-10 rounded-full px-4 py-1.5 text-[12px] font-[700] transition-colors duration-300 ease-out " +
                          (deliveryType !== "pickup"
                            ? "text-[#111827]"
                            : "text-[#111827] bg-transparent") +
                          (isDeliveryDisabled
                            ? " opacity-50 cursor-not-allowed"
                            : "")
                        }
                      >
                        Delivery
                      </button>

                      <button
                        type="button"
                        disabled={isPickupDisabled}
                        onClick={() => changeDeliveryType("pickup")}
                        className={
                          "relative z-10 rounded-full px-4 py-1.5 text-[12px] font-[700] transition-colors duration-300 ease-out " +
                          (deliveryType === "pickup"
                            ? "text-[#111827]"
                            : "text-[#111827] bg-transparent") +
                          (isPickupDisabled
                            ? " opacity-50 cursor-not-allowed"
                            : "")
                        }
                      >
                        Pickup
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setDeleteDialog("ALL")}
                    className="px-3.5 py-1.5 rounded-full bg-gray-50 text-gray-800 border border-gray-200 font-extrabold text-[12px] shadow-sm active:scale-[0.99]"
                  >
                    Clear
                  </button>
                </div>

                <div className="mt-2.5 px-1">
                  <p className="text-[12px] font-semibold text-gray-700">
                    {deliveryType === "pickup"
                      ? "You have selected Pick up."
                      : "You have selected Delivery."}
                  </p>

                  {deliveryType === "pickup" && (
                    <div
                      className="mt-2 overflow-hidden rounded-xl border"
                      style={{
                        borderColor: "rgb(var(--color-primary) / 0.16)",
                        background:
                          "linear-gradient(180deg, rgb(var(--color-primary) / 0.05), rgb(255 255 255 / 0.92))",
                      }}
                    >
                      <div className="flex items-start gap-2 px-3 py-3">
                        <LocationOnOutlinedIcon
                          className="!text-[18px] shrink-0 mt-[1px]"
                          sx={{ color: "rgb(31 41 55 / 0.92)" }}
                        />
                        <div className="min-w-0">
                          <p className="text-[13px] font-bold text-gray-900 break-words">
                            Pick up • {companyHeaderName}
                          </p>
                          <p className="text-[12px] text-gray-800 break-words leading-[1.45]">
                            {pickupLocationAddress}
                          </p>
                        </div>
                      </div>

                      {!!scheduledText && (
                        <div className="flex items-center gap-2 border-t border-black/5 bg-black/[0.03] px-3 py-2">
                          <AccessTimeOutlinedIcon
                            className="!text-[17px] shrink-0"
                            sx={{ color: "rgb(31 41 55 / 0.92)" }}
                          />
                          <div className="min-w-0 flex-1 text-[12px] text-gray-800 break-words">
                            <span className="font-semibold">Scheduled: </span>
                            <span className="font-bold">{scheduledText}</span>
                          </div>
                          <KeyboardArrowRightIcon className="!text-[18px] text-gray-700 shrink-0" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Items list */}
          <div
            className="py-1 flex-1 overflow-y-auto"
            style={{
              paddingBottom: `calc(${footerHeight}px + env(safe-area-inset-bottom) + 12px)`,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {cart.map((item, index) => (
              <Item
                key={`${item?.id || item?.productId || item?.fullDescription || "item"}-${index}`}
                item={item}
                index={index}
                onAction={handleCartAction}
                showDefaultToppings={settings?.DisplayDefaultToppings}
              />
            ))}
            {!!cart.length && (!!relatedProducts.length || relatedLoading) && (
              <div className="px-3 pt-4 pb-2" style={{ fontFamily: cleanFontStack }}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-[700] uppercase tracking-[0.18em] text-gray-400">
                      You May Also Like
                    </div>
                    <div className="mt-1 text-[13px] font-[700] tracking-[-0.01em] text-gray-900">
                      Popular picks from the menu
                    </div>
                  </div>
                </div>

                {relatedLoading ? (
                  <div className="text-[12px] font-semibold text-gray-500 px-1 py-3">
                    Loading suggestions...
                  </div>
                ) : (
                  <>
                    <div
                      ref={relatedRowRef}
                      className="cart-related-strip flex gap-2 overflow-x-auto pb-1 scroll-smooth"
                      style={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        cursor: "grab",
                        userSelect: "none",
                        WebkitOverflowScrolling: "touch",
                        touchAction: "pan-x",
                      }}
                      onWheel={handleRelatedWheel}
                      onMouseDown={handleRelatedMouseDown}
                      onMouseMove={handleRelatedMouseMove}
                      onMouseUp={() => {
                        stopRelatedDrag();
                        resumeRelatedAutoScroll();
                      }}
                      onMouseLeave={() => {
                        stopRelatedDrag();
                        resumeRelatedAutoScroll();
                      }}
                      onMouseEnter={pauseRelatedAutoScroll}
                      onTouchStart={pauseRelatedAutoScroll}
                      onTouchEnd={resumeRelatedAutoScroll}
                      onTouchCancel={resumeRelatedAutoScroll}
                    >
                      {relatedProducts.map((item: any, index: number) => {
                        const image = getCartItemImage(item);
                        const visiblePriceText = getSuggestedProductSubPriceText(item);

                        return (
                          <button
                            key={`${item?.productId || item?.id || "related"}-${index}`}
                            type="button"
                            onClick={() => openSuggestedProduct(item)}
                            className="w-[122px] shrink-0 rounded-[16px] border p-2 text-left shadow-sm transition-all duration-200 active:scale-[0.98] active:shadow-[0_14px_28px_rgba(15,23,42,0.14)]"
                            style={{
                              fontFamily: cleanFontStack,
                              background:
                                "linear-gradient(180deg, rgb(255 255 255 / 0.88), rgb(255 255 255 / 0.72))",
                              borderColor: "rgb(255 255 255 / 0.65)",
                              backdropFilter: "blur(12px)",
                              WebkitBackdropFilter: "blur(12px)",
                              boxShadow: "0 10px 24px rgb(15 23 42 / 0.08)",
                            }}
                          >
                            <div className="h-[62px] w-full overflow-hidden rounded-[12px] bg-gray-100">
                              {image ? (
                                <img
                                  src={image}
                                  alt={item?.productName || "Product"}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-gray-500">
                                  No Image
                                </div>
                              )}
                            </div>
                            <div className="mt-2 text-[8px] font-[700] uppercase tracking-[0.1em] text-gray-500 truncate">
                              {item?.__categoryName || "Category"}
                            </div>
                            <div className="mt-1 line-clamp-2 text-[10px] font-[700] leading-[1.25] tracking-[-0.01em] text-gray-900">
                              {item?.productName}
                            </div>
                            <div className="mt-1.5 flex items-center justify-between gap-2">
                              <div className="min-h-[18px] line-clamp-1 text-[10px] font-[700] text-gray-900">
                                {visiblePriceText}
                              </div>
                              <div
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border text-[18px] font-semibold leading-none shadow-md"
                                style={{
                                  backgroundColor:
                                    "rgba(var(--color-card, 255,255,255), 0.92)",
                                  border: "1px solid rgba(var(--color-primary, 0,0,0), 0.10)",
                                  color: "#000000",
                                }}
                              >
                                +
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <style>{`.cart-related-strip::-webkit-scrollbar{display:none;height:0}`}</style>
                  </>
                )}
              </div>
            )}
            {!cart.length && <p className="text-center">No Item Selected</p>}
          </div>

          {/* Footer */}
          <div
            ref={footerRef}
            className="shrink-0 sticky bottom-0 z-20 border-t border-gray-200"
            style={{
              backgroundColor: "rgb(255 255 255 / 0.96)",
              backdropFilter: "blur(10px)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <div className="px-3 pt-1 pb-1">
              <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white px-2 py-1.5 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                <Button
                  disabled={!cart.length}
                  className="!rounded-full !px-6 !py-3.5 !font-[800] !bg-primary !text-themeSelColor !border-0 !shadow-none !min-w-[150px]"
                  onClick={placeOrder}
                  style={{ border: "none" }}
                >
                  Place Order
                </Button>

                <div className="flex-1 min-w-0 px-1 text-right">
                  <div className="text-[11px] font-[800] uppercase tracking-[0.14em] text-gray-500">
                    Subtotal
                  </div>
                  <div className="mt-0.5 text-[28px] font-[900] leading-none tracking-[-0.03em] text-gray-900">
                    ${subTotal.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Optional breakdown (enable later) */}
              {SHOW_PRICE_BREAKDOWN && (
                <div className="mt-2">
                  {finalBill.map((i) => (
                    <>
                      {i.amt > 0 && (
                        <div className="flex items-center py-1">
                          <p className="font-bold text-[13px] text-gray-600">
                            {i.title}
                          </p>
                          <DividerDot />
                          <p className="font-bold text-[13px] text-gray-600">
                            ${i.amt.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </>
                  ))}

                  {!!deliveryCharge && (
                    <div className="flex items-center py-1">
                      <p className="font-bold text-[13px] text-gray-600">
                        Delivery Charge
                      </p>
                      <DividerDot />
                      <p className="font-bold text-[13px] text-gray-600">
                        ${deliveryCharge.toFixed(2)}
                      </p>
                    </div>
                  )}

                  {orderType === ORDER.DELIVERY &&
                    isKmDeliveryChargesEnabled(settings) && (
                      <div className="flex items-center py-1">
                        <p className="font-bold text-[13px] text-gray-600">
                          Delivery Charge
                        </p>
                        <DividerDot />
                        <p className="font-bold text-[13px] text-gray-600">
                          Calculated at checkout
                        </p>
                      </div>
                    )}

                  {/* Total (after tax + delivery) */}
                  <div className="flex items-center py-1">
                    <p className="font-extrabold text-[13px] text-gray-900">
                      Total
                    </p>
                    <DividerDot />
                    <p className="font-extrabold text-[13px] text-gray-900">
                      ${(total + deliveryCharge).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Remove dialog */}
      <Dialog
        onClose={() => setDeleteDialog(undefined)}
        open={!!deleteDialog}
        hideBackdrop
        sx={{
          zIndex: 10050,
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            justifyContent: "flex-end",
            paddingTop: "calc(env(safe-area-inset-top) + 16px)",
            paddingRight: "16px",
            paddingLeft: "16px",
          },
        }}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 380,
            borderRadius: "14px",
            border: "1px solid rgb(229 231 235)",
            boxShadow: "0 18px 60px rgb(0 0 0 / 0.20)",
            backgroundColor: "rgb(255 255 255 / 0.96)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <DialogTitle className="!font-bold">Remove from cart</DialogTitle>
        <DialogContent>
          <p>
            are you sure you want to remove{" "}
            {deleteDialog === "ALL"
              ? "All Items"
              : deleteDialog?.item.productName}
            {` `}
            from cart ?
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            className="bg-white !text-gray-800 !border-0"
            onClick={() => setDeleteDialog(undefined)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteDialog === "ALL") {
                dispatch(removeAllFromCart());
              } else {
                dispatch(removeFromCart(deleteDialog.index));
              }
              setDeleteDialog(undefined);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CartView;
